// Locale configuration for the public site.
// English lives at the root, other locales behind a URL prefix so every
// language version is its own indexable address.

export const LOCALES = ["en", "no", "zh-Hant"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/** URL prefix per locale ("" for the default). */
export const LOCALE_PREFIX: Record<Locale, string> = {
  en: "",
  no: "/no",
  "zh-Hant": "/zh-hant",
};

/** Value for <html lang>. */
export const HTML_LANG: Record<Locale, string> = {
  en: "en",
  no: "nb",
  "zh-Hant": "zh-Hant",
};

/** hreflang values emitted per locale (first one is the canonical alternate). */
export const HREFLANGS: Record<Locale, string[]> = {
  en: ["en"],
  no: ["nb", "no"],
  "zh-Hant": ["zh-Hant", "zh-HK", "zh-TW"],
};

export const OG_LOCALE: Record<Locale, string> = {
  en: "en_US",
  no: "nb_NO",
  "zh-Hant": "zh_TW",
};

export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English",
  no: "Norsk",
  "zh-Hant": "繁體中文",
};

export const LOCALE_FLAG: Record<Locale, string> = {
  en: "🇬🇧",
  no: "🇳🇴",
  "zh-Hant": "🇭🇰",
};

/** Language code used by the article translation engine. */
export const TRANSLATION_CODE: Record<Locale, string | null> = {
  en: null,
  no: "no",
  "zh-Hant": "zh-Hant",
};

const isLocale = (value: string): value is Locale =>
  (LOCALES as readonly string[]).includes(value);

/** Split "/zh-hant/feed" into { locale: "zh-Hant", pathname: "/feed" }. */
export function splitLocalePath(pathname: string): { locale: Locale; pathname: string } {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  for (const locale of LOCALES) {
    const prefix = LOCALE_PREFIX[locale];
    if (!prefix) continue;
    if (normalized.toLowerCase() === prefix) return { locale, pathname: "/" };
    if (normalized.toLowerCase().startsWith(`${prefix}/`)) {
      return { locale, pathname: normalized.slice(prefix.length) || "/" };
    }
  }
  return { locale: DEFAULT_LOCALE, pathname: normalized };
}

/** Add the locale prefix to an app path. */
export function localizePath(pathname: string, locale: Locale): string {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const prefix = LOCALE_PREFIX[locale];
  if (!prefix) return normalized;
  if (normalized === "/") return prefix;
  return `${prefix}${normalized}`;
}

export function localeFromString(value: string | null | undefined): Locale {
  if (value && isLocale(value)) return value;
  return DEFAULT_LOCALE;
}
